import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal, NgbModalRef } from '@cmps/ng-bootstrap';
import { concat, interval, Observable, of, Subscription } from 'rxjs';
import {
    catchError,
    concatMap,
    delay,
    filter,
    map,
    retryWhen,
    scan,
    switchMap,
    take,
    tap,
} from 'rxjs/operators';

import { AuthStatus } from '../auth/auth.model';
import { AuthService } from '../auth/auth.service';
import { DrawerOptions } from '../common/drawer.directive';
import { ScreenReaderLiveService } from '../common/screen-reader-live.service';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../common/take-until-destroy.decorator';

import { ModalNotificationComponent } from './modal.notification.component';
import { NavigationSrc, NavigationSrcQueryParam } from './navigate';
import { KeyboardNavigator } from './notification-panel.keyboard';
import { NotificationPanelService } from './notification-panel.service';
import { NotificationEvent } from './notification.component';
import { TranslationKeyMap } from './notification.i18n';
import {
    Notification,
    NotificationCategory,
    NotificationCategoryList,
    NotificationPriority,
} from './notification.model';
import { NotificationService } from './notification.service';

@Component({
    selector: 'cmps-notification-panel',
    templateUrl: './notification-panel.component.html',
    host: {
        '(click)': '_keyboardNavigator.select($event)',
        '(keydown)': '_keyboardNavigator.handleEvent($event)',
    },
})
@TakeUntilDestroy()
export class NotificationPanelComponent
    implements OnInit, OnDestroy, TakeUntilDestroy
{
    i18n = TranslationKeyMap;
    _NotificationCategoryList = NotificationCategoryList;
    drawerOptions: DrawerOptions;
    hasPendingData: boolean;
    categories: NotificationCategory[];
    categoryCount: number[];
    /** @private */ _unreadAlertCount: number;
    private _open: boolean;
    private _sub: Subscription;
    private _modal: NgbModalRef;
    private readonly _keyboardNavigator: KeyboardNavigator;

    get open(): boolean {
        return this._open;
    }

    set open(value: boolean) {
        const toggled = this._open !== value;
        this._open = value;
        if (toggled) {
            this._markUnAcknowledged();
            this._service.toggle.emit(this._open);
        }
    }

    get notifications(): Notification[] {
        return this._service.notifications;
    }

    constructor(
        private readonly _el: ElementRef,
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _modalService: NgbModal,
        private readonly _authService: AuthService,
        private _service: NotificationPanelService,
        private readonly _notificationService: NotificationService,
        private readonly _screenReaderLiveService: ScreenReaderLiveService,
    ) {
        this.drawerOptions = {
            property: 'right',
            valueOpen: '0',
            valueClose: '-360px',
            focusOpen: '#notificationPanel',
            focusClose: '[aria-controls=notificationPanel]',
        };
        _service.register(this);

        this._keyboardNavigator = new KeyboardNavigator(_el.nativeElement);
    }

    ngOnInit(): void {
        this.load(false)
            // eslint-disable-next-line n/handle-callback-err
            .subscribe(null, (error) => null);

        this._service.notificationChange
            .pipe(
                tap(() => {
                    this._checkUnreadAlerts();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        if (this._sub) {
            this._sub.unsubscribe();
        }
        this._service.register(null);
    }

    load(forceReload: boolean): Observable<NotificationCategory[]> {
        this.categories = null;
        this._service.notifications = null;

        if (this._sub) {
            this._sub.unsubscribe();
        }

        let didFirstRun = false;
        this.hasPendingData = true;
        return this._notificationService.listCategories(forceReload).pipe(
            map((categories) => {
                this.categories = categories || [];
                this.categoryCount = [];
                // eslint-disable-next-line no-return-assign
                this.categories.forEach(
                    (value) => (this.categoryCount[value.id] = 0),
                );
                return categories;
            }),
            catchError((error) => {
                console.error(error);
                this.categories = this.categories || [];
                this.categoryCount = [];
                return of(this.categories);
            }),
            tap(() => {
                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                const canContinue = () => {
                    const authenticated =
                        this._authService.authStatus.value ===
                        AuthStatus.AUTHENTICATED;
                    return authenticated && this._modal == null;
                };

                const canShowAlerts = this._activatedRoute.queryParamMap.pipe(
                    take(1),
                    map((queryParams) => {
                        const alertsEnabled =
                            queryParams.get(NavigationSrcQueryParam) !==
                            NavigationSrc.NOTIFICATION_ALERT;
                        return canContinue() && alertsEnabled;
                    }),
                );

                this._sub = concat(of(null) /* immediate */, interval(120000))
                    .pipe(
                        filter(() => canContinue()),
                        switchMap(() =>
                            this._notificationService.list().pipe(
                                retryWhen((errors) =>
                                    errors.pipe(
                                        delay(10000),
                                        scan((acc) => {
                                            if (acc >= 2 || !canContinue()) {
                                                throw new Error(
                                                    'fast retry failed',
                                                );
                                            }
                                            return acc + 1;
                                        }, 0),
                                    ),
                                ),
                                catchError((error) => {
                                    console.error(error);
                                    return of(null);
                                }),
                            ),
                        ),
                        map((notifications: Notification[]) => {
                            notifications = notifications || [];
                            this._service.notifications =
                                this._service.notifications || [];

                            let changed = [];
                            this._service.notifications.forEach((value) => {
                                const n = notifications.find(
                                    (value2) => value2.id === value.id,
                                );
                                if (!n) return;
                                value.merge(n);
                                changed.push(value);
                            });

                            const changedIds = changed.map((value) => value.id);
                            // eslint-disable-next-line @typescript-eslint/prefer-includes
                            const new_ = notifications.filter(
                                // eslint-disable-next-line @typescript-eslint/prefer-includes
                                (value) => changedIds.indexOf(value.id) === -1,
                            );
                            new_.reverse();
                            new_.forEach((value) => {
                                this._service.notifications.unshift(value);
                                if (this.categoryCount.length > 0) {
                                    this.categoryCount[value.categoryId]++;
                                }
                            });

                            changed = changed.concat(new_);
                            if (changed.length > 0) {
                                this._service.notificationChange.emit(changed);
                            }

                            this._checkUnreadAlerts();

                            return new_;
                        }),
                        concatMap((new_) => {
                            // TODO revise. see service.markAsAcknowledged, service.markAsRead
                            const pendingAlerts =
                                this._service.notifications.filter((value) => {
                                    return (
                                        (value.priorityId ===
                                            NotificationPriority.CRITICAL ||
                                            value.requiresAcknowledgement) &&
                                        !value.wasAcknowledged
                                    );
                                });
                            if (
                                didFirstRun &&
                                new_.length !== 0 &&
                                pendingAlerts.length === 0
                            ) {
                                // TODO revise tl
                                this._screenReaderLiveService.announce(
                                    new_.length === 1
                                        ? 'New notification received'
                                        : `${new_.length} new notifications received`,
                                );
                                return of(true);
                            }
                            didFirstRun = true;
                            return canShowAlerts.pipe(
                                tap((result) => {
                                    if (result) {
                                        this._showAlerts(pendingAlerts);
                                    }
                                }),
                            );
                        }),
                        catchError((error) => {
                            console.error(error);
                            this._service.notifications =
                                this._service.notifications || [];
                            this._unreadAlertCount = 0;
                            return of(null);
                        }),
                        // eslint-disable-next-line no-return-assign
                        tap(() => (this.hasPendingData = false)),
                    )
                    .subscribe();
            }),
        );
    }

    onNotificationDismiss(event: NotificationEvent): void {
        const i = this._service.notifications.indexOf(event.notification);
        if (i !== -1) {
            this._service.notifications.splice(i, 1);
        }
    }

    private _markUnAcknowledged(): void {
        if (!this._service.notifications) return;
        this._service.notifications.forEach((notification) =>
            this._service.markAsAcknowledged(notification),
        );
    }

    private _showAlerts(pendingAlerts: Notification[]): void {
        if (this._modal != null) return;
        if (this._authService.authStatus.value !== AuthStatus.AUTHENTICATED)
            return;
        if (pendingAlerts.length === 0) return;
        this._modalService.dismissAll();
        this._modal = this._modalService.open(ModalNotificationComponent, {
            backdrop: 'static',
            size: 'lg',
            windowClass: 'cmps-notification-modal-window',
        });
        this._modal.componentInstance.vm.notifications = pendingAlerts;
        // eslint-disable-next-line no-return-assign
        this._modal.result.then(
            (result) => (this._modal = null),
            (reason) => (this._modal = null),
        );
    }

    private _checkUnreadAlerts(): void {
        this._unreadAlertCount = this._service.notifications.filter(
            (x) =>
                x.categoryId === NotificationCategoryList.ALERT &&
                (!x.readOn || isNaN(x.readOn.getTime())),
        ).length;
    }
}
